// frontend/src/hooks/useDayRisk.ts
//
// Today's safety limits from the server (GET /api/day-risk/status): the one
// account size and risk-per-trade figure every screen sizes from (the ticket,
// the Pro desk header, the Simple desk's safety limit), plus whether the desk
// is LOCKED for the day. Re-read every 30 s, after a trade is logged
// (TRADES_CHANGED) and after the limits are edited (DAY_RISK_CHANGED).
import { useCallback, useEffect, useState } from "react";
import type { DailyRiskStatus } from "@/components/daytrader/dayTrader";
import { TRADES_CHANGED } from "@/components/desk/deskData";

/** Fired after the daily limits are saved, so every panel re-reads them. */
export const DAY_RISK_CHANGED = "vp:day-risk-changed";

export function useDayRiskStatus(pollMs = 30_000) {
  const [status, setStatus] = useState<DailyRiskStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const res = await fetch("/api/day-risk/status");
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "status unavailable");
      setStatus(json.status as DailyRiskStatus);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load today's limits");
    }
  }, []);

  useEffect(() => {
    reload();
    const id = setInterval(reload, pollMs);
    window.addEventListener(TRADES_CHANGED, reload);
    window.addEventListener(DAY_RISK_CHANGED, reload);
    return () => {
      clearInterval(id);
      window.removeEventListener(TRADES_CHANGED, reload);
      window.removeEventListener(DAY_RISK_CHANGED, reload);
    };
  }, [reload, pollMs]);

  return { status, error, reload };
}
